import React, { useState } from "react";
import { View } from "react-native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { LinearGradient } from "expo-linear-gradient";
import ChatList from "./ChatList";
import ChatInput from "./ChatInput";
import LikesHeader from "./LikesHeader";
import CompactPoll from "./CompactPoll";
import StreamSharePopup from "./StreamSharePopup";
import LikeAnimation from "../../../../../../Components/live-streaming-common-components/LikeAnimation";
import { ChatMessage } from "../../types";

interface SoloStreamChatProps {
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
  likes: number;
  onLike: () => void;
  totalViewers: number;
  isMobile: boolean;
  onShare: (platform: string) => void; // Using simplified handler or redirecting to popup
  onGift?: () => void;
  // Props for share popup/modal management if needed, but we can manage local popup state here
  // or accept a handler to open global modal
  onOpenShareModal: () => void;
  streamId: string;
}

const SoloStreamChat: React.FC<SoloStreamChatProps> = ({
  messages,
  onSendMessage,
  likes,
  onLike,
  totalViewers,
  isMobile,
  onOpenShareModal,
  onGift,
  streamId,
}) => {
  const deviceSize = useDeviceSize();
  const [likeAnimations, setLikeAnimations] = useState<number[]>([]);
  const [showSharePopup, setShowSharePopup] = useState(false);

  // Handle local like animation + trigger prop
  const handleLike = () => {
    const animationId = Date.now();
    setLikeAnimations((prev) => [...prev, animationId]);
    onLike();
  };

  const handleAnimationComplete = (id: number) => {
    setLikeAnimations((prev) => prev.filter((animId) => animId !== id));
  };

  return (
    <View
      style={{
        flex: 1,
        // height: "100%", // Controlled by parent
      }}
    >
      {/* Desktop Layout (XL) */}
      {deviceSize === DEVICE_SIZES.XL ? (
        <View
          style={{
            backgroundColor: "black",
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          {/* Header */}
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "#374151",
              padding: 6,
              paddingHorizontal: 12,
              height: 60,
            }}
          >
            <LikesHeader
              likes={likes}
              totalViewers={totalViewers}
              viewerDetails={[]} // Mocked/Empty for now unless passed down
            />
          </LinearGradient>

          <View style={{ height: 6 }} />

          {/* Chat List */}
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "#374151",
              padding: 6,
              paddingHorizontal: 12,
              flex: 1,
            }}
          >
            <ChatList messages={messages} isMobile={false} />

            {/* Input inside the chat container for Desktop */}
            <ChatInput
              onSendMessage={onSendMessage}
              onLike={handleLike}
              onShare={() => setShowSharePopup(!showSharePopup)}
              isMobile={false}
              canSend={true}
            />

            {/* Like Animations Overlay */}
            {likeAnimations.map((id) => (
              <LikeAnimation
                key={id}
                onComplete={() => handleAnimationComplete(id)}
              />
            ))}
          </LinearGradient>

          <View style={{ height: 6 }} />

          {/* Poll / Share Popup Section */}
          <View
            style={{
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "#374151",
              backgroundColor: "rgba(0, 0, 0, 0.20)",
              height: "20%",
              overflow: "hidden",
            }}
          >
            {showSharePopup ? (
              <StreamSharePopup
                onClose={() => setShowSharePopup(false)}
                streamId={streamId}
              />
            ) : (
              <CompactPoll />
            )}
          </View>
        </View>
      ) : (
        // Mobile Layout
        <View style={{ flex: 1 }}>
          {/* Header */}
          <View
            style={{
              height: 39,
              marginBottom: 8,
              borderColor: "white",
              borderWidth: 1,
              borderRadius: 6,
              padding: 4,
            }}
          >
            <LikesHeader
              likes={likes}
              totalViewers={totalViewers}
              viewerDetails={[]}
            />
          </View>

          {/* Chat List */}
          <View style={{ flex: 1 }}>
            <ChatList messages={messages} isMobile={true} />

            {/* Like Animations Overlay (positioned relative to container) */}
            {likeAnimations.map((id) => (
              <LikeAnimation
                key={id}
                onComplete={() => handleAnimationComplete(id)}
              />
            ))}
          </View>

          {/* Input Area */}
          <ChatInput
            onSendMessage={onSendMessage}
            onLike={handleLike}
            onShare={onOpenShareModal} // On mobile, typically opens global modal
            onGift={onGift}
            isMobile={true}
            canSend={true}
          />
        </View>
      )}
    </View>
  );
};

export default SoloStreamChat;
